import { useEffect } from 'react'
import { useUserProfile } from 'core/hooks/data/useDataHook'
import useRolesAndPermissions from 'core/hooks/user/useRolesAndPermissions'
import { useQuery } from '@tanstack/react-query'
import APIService from 'api/APIService'
import Intercom, { update } from '@intercom/messenger-js-sdk'
import { cleanObject } from 'utils/Functions'
import {
  IntercomSettings,
  UserArgs
} from '@intercom/messenger-js-sdk/dist/types'

const INTERCOM_APP_ID = import.meta.env.VITE_APP_INTERCOM_APP_ID
const INTERCOM_MESSENGER_ENV = import.meta.env.VITE_APP_INTERCOM_MESSENGER_ENV

const intercomSettings = {
  api_base: 'https://api-iam.intercom.io',
  app_id: INTERCOM_APP_ID,
  messenger_environment: INTERCOM_MESSENGER_ENV,
  hide_default_launcher: false
} as IntercomSettings

const useFinChatbot = () => {
  const { data: currentUser } = useUserProfile()
  const { hasTeam } = useRolesAndPermissions()
  const intercomEnabled = !!currentUser?.intercom_enabled

  const { data: intercom } = useQuery({
    queryKey: ['users', 'intercom_token'],
    queryFn: APIService.users.getIntercomToken,
    enabled: intercomEnabled
  })

  // Boot once user data is ready — equivalent to mobile's loginIdentifiedUser
  useEffect(() => {
    if (!intercomEnabled || !intercom) return

    Intercom({
      ...intercomSettings,
      intercom_user_jwt: intercom.token
    })
  }, [intercomEnabled, intercom])

  useEffect(() => {
    const { token_id, intercom_enabled, email, name } = currentUser || {}
    if (!intercom_enabled || !token_id || !intercom) return

    const iosSubscription =
      currentUser?.subscription?.type === 'IapSubscription' ? 'Apple' : null
    const googleSubscription =
      currentUser?.subscription?.type === 'GoogleSubscription' ? 'Google' : null
    const stripeSubscription =
      currentUser?.subscription?.type === 'StripeSubscription' ? 'Stripe' : null

    const roles =
      (hasTeam && currentUser?.teams?.[0].roles?.map((role) => role.name)) ||
      null

    const customAttributes = {
      // App info
      auto_detect_enabled: !!currentUser?.auto_detect_enabled,
      auto_classify: currentUser?.auto_classify === 'ON',
      kms_enabled: !!currentUser?.kms_enabled,
      beacon_enabled: !!currentUser?.beacon_enabled,
      ios_app_version: currentUser?.ios_app_version || null,
      ios_device_version: currentUser?.ios_device_version || null,
      android_app_version: currentUser?.android_app_version || null,
      android_device_version: currentUser?.android_device_version || null,
      // General user
      group_as_cobrand_member:
        currentUser?.group_as_cobrand_member?.company_name || null,
      persona: currentUser?.persona || null,
      is_team_member: hasTeam,
      language_override: navigator.language,

      // B2C User
      subscription_status: !!currentUser?.subscription?.token_id,
      subscription_type: currentUser?.subscription?.product_identifier || null,
      subscription_source:
        iosSubscription || googleSubscription || stripeSubscription,
      referral_code_name: currentUser?.referral_code_name || null,
      invited_by_code: currentUser?.invited_by_code || null,
      trial_days_remaining: currentUser?.trial_days_remaining || null,
      trial_trips_remaining: currentUser?.free_trips_remaining || null,

      // B2B User
      role: roles,
      program_type: currentUser?.program?.program_type || null,
      variable_rate:
        currentUser?.program?.effective_rate?.miles_values?.rate || null,
      fixed_rate:
        currentUser?.program?.effective_rate?.miles_values?.fixed_rate || null
    }

    const cleanCustomAttributes = cleanObject(customAttributes)
    const createdAt = currentUser?.created_at
      ? Math.floor(new Date(currentUser.created_at).getTime() / 1000)
      : undefined
    const args: UserArgs = {
      ...intercomSettings,
      intercom_user_jwt: intercom.token,
      user_id: token_id as string,
      email: email ?? undefined,
      name: name ?? undefined,
      created_at: createdAt,
      ...cleanCustomAttributes
    }

    update(args)
  }, [currentUser, hasTeam, intercom])

  return null
}

export default useFinChatbot
